import { useCallback, useEffect, useRef, useState } from "react";
import { readUserCache, writeUserCache } from "../lib/dashboardCache";

export type ResourceCategory = "tutorial" | "concept" | "troubleshooting" | "update" | "inspiration";
export type ResourceFormat = "video" | "article" | "prompt";
// Allowlisted embed hosts. Mirrors the backend's VideoProvider enum, which is
// what decides whether a URL is accepted at all.
export type VideoProvider = "loom" | "youtube";

export type ResourceView = {
  id: string;
  slug: string;
  title: string;
  description: string | null;
  category: ResourceCategory;
  format: ResourceFormat;
  videoProvider: VideoProvider | null;
  videoId: string | null;
  videoUrl: string | null;
  durationSeconds: number | null;
  thumbnailUrl: string | null;
  bodyMarkdown: string | null;
  // Slug of the integration this resource guides, or null for a general
  // library resource. Renders as the "// GUIDE" block on that integration.
  integrationSlug: string | null;
  published: boolean;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
};

export type ResourcesStatus = "loading" | "ready" | "error";

export type UseResourcesResult = {
  status: ResourcesStatus;
  resources: ReadonlyArray<ResourceView>;
  error: string | null;
  refresh: () => Promise<void>;
};

const LIST_URL = "/api/resources";

const RESOURCES_RESOURCE = "resources";

export const useResources = (): UseResourcesResult => {
  const [cached] = useState<ReadonlyArray<ResourceView> | null>(() =>
    readUserCache<ResourceView[]>(RESOURCES_RESOURCE),
  );
  const [status, setStatus] = useState<ResourcesStatus>(cached !== null ? "ready" : "loading");
  const [resources, setResources] = useState<ReadonlyArray<ResourceView>>(cached ?? []);
  const [error, setError] = useState<string | null>(null);
  // Bumped on every refresh; only the latest call is allowed to setState
  // so concurrent refreshes (e.g. focus + manual) can't clobber each other.
  const reqId = useRef<number>(0);
  const mounted = useRef<boolean>(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const refresh = useCallback(async (): Promise<void> => {
    const id = ++reqId.current;
    try {
      const res = await fetch(LIST_URL, { credentials: "include" });
      if (!mounted.current || id !== reqId.current) return;
      if (res.status === 200) {
        const body = (await res.json()) as ResourceView[];
        if (!mounted.current || id !== reqId.current) return;
        setResources(body);
        setStatus("ready");
        setError(null);
        writeUserCache(RESOURCES_RESOURCE, body);
      } else {
        setStatus("error");
        setError(`List failed (${res.status})`);
      }
    } catch (err) {
      if (!mounted.current || id !== reqId.current) return;
      const message = err instanceof Error ? err.message : String(err);
      setStatus("error");
      setError(message);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return { status, resources, error, refresh };
};

export type UseResourceResult = {
  status: ResourcesStatus | "not_found";
  resource: ResourceView | null;
  error: string | null;
};

export const useResourceBySlug = (slug: string | undefined): UseResourceResult => {
  const [status, setStatus] = useState<ResourcesStatus | "not_found">("loading");
  const [resource, setResource] = useState<ResourceView | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!slug) {
      setStatus("not_found");
      setResource(null);
      return;
    }
    let cancelled = false;
    setStatus("loading");
    void (async () => {
      try {
        const res = await fetch(`${LIST_URL}/${encodeURIComponent(slug)}`, {
          credentials: "include",
        });
        if (cancelled) return;
        if (res.status === 200) {
          setResource((await res.json()) as ResourceView);
          setStatus("ready");
          setError(null);
        } else if (res.status === 404) {
          setStatus("not_found");
          setResource(null);
          setError(null);
        } else {
          setStatus("error");
          setError(`Failed (${res.status})`);
        }
      } catch (err) {
        if (cancelled) return;
        setStatus("error");
        setError(err instanceof Error ? err.message : String(err));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  return { status, resource, error };
};
